import React, { useState } from 'react';
import { Grid } from '@material-ui/core';
import { useTranslation } from '@michelin/central-provider';
import {
  TextField, Button, useDialogs, Panel, Tooltip,
} from '@michelin/acid-components';
import {
  useForm, Controller,
} from 'react-hook-form';
import PreventTransitionDialog from '@michelin/acid-components/build/components/PreventTransitions/PreventTransitionDialog';
import { Warning } from '@material-ui/icons';
import { CSSProperties } from 'styled-components';
import { scrollToDisplayFirstError } from 'Utils';
import FileUpload from '../../FileUpload/FileUpload';
import { useSavePromo } from '../../../hooks/useAdminQueries';

interface SelfServicePromoCodesModalProps {
  onChange: (data:any) => void,
  disabled: boolean,
}

function UploadForm(props:SelfServicePromoCodesModalProps) {
  const { t } = useTranslation();
  const { errorDialog } = useDialogs();
  const { disabled } = props;

  const {
    register, formState: { errors, isDirty, isSubmitting }, control, handleSubmit, reset,
  } = useForm<any>({ defaultValues: { name: '' } });

  const [preventTransition, setPreventTransition] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const mutatePromo = useSavePromo();

  const errorStyle: CSSProperties = {
    color: 'rgb(241, 111, 111)', marginTop: '5px', verticalAlign: 'bottom', cursor: 'pointer',
  };

  const save = async (data: any) => {
    try {
      await mutatePromo.mutateAsync({ data });
      reset();
      props.onChange(data);
    } catch (e) {
      errorDialog(
        t('Please check your connection and try again.'),
        t('Error saving data.'),
      );
    }
  };

  const onError = () => {
    errorDialog(
      t('Please check the marked fields.').split('\n'),
      t('Error saving data  .'),
    );
    scrollToDisplayFirstError();
  };

  return (
    <Panel
      title={t('Upload a new file')}
      control={(
        <Tooltip
          placement="top"
          arrow
          title={t('Uploading a new file is disabled while processing an existing one') || ''}
        >
          <Warning style={errorStyle} />
        </Tooltip>
      )}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} style={{ marginTop: 7 }}>
          <TextField
            label={t('Promo Name')}
            error={errors.name}
            inputProps={{
              ...register('name', {
                required: true,
              }),
            }}
            readOnly={disabled}
            required
          />
        </Grid>
        <Grid item xs={12} style={{ marginTop: 7 }}>
          <Controller
            control={control}
              /* eslint-disable-next-line react/jsx-props-no-spreading */
            {...register('file', { required: true })}
            render={(item) => (
              <FileUpload
                error={errors.file}
                data={item.field.value}
                onChange={item.field.onChange}
                onUploading={setUploading}
                label={t('Promo File (Acceptable file type is .csv only).')}
                required
                readOnly={disabled}
                mode="minimal"
                acceptedFiles={['text/csv', 'application/vnd.ms-excel']}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs="auto">
          <Button
            size="small"
            color="primary"
            disabled={!isDirty || uploading || disabled || isSubmitting}
            onClick={handleSubmit(save, onError)}
          >
            {t('Add')}
          </Button>
        </Grid>
        <Grid item xs="auto">
          <Button
            size="small"
            color="default"
            disabled={isSubmitting || uploading || disabled}
            onClick={() => {
              if (isDirty) setPreventTransition(true);
              else {
                reset();
                props.onChange(null);
              }
            }}
          >
            {t('Cancel')}
          </Button>
        </Grid>
      </Grid>
      <PreventTransitionDialog
        open={preventTransition}
        handleCancel={() => { setPreventTransition(false); }}
        handleClose={() => { setPreventTransition(false); reset(); props.onChange(null); }}
        handleDiscard={() => { setPreventTransition(false); reset(); props.onChange(null); }}
        message={t('You have unsaved changes...')}
        title={t('Do you want to discard those changes?')}
      />
    </Panel>
  );
}

export {
  UploadForm,
};
